// Import all repositories
import {
  UserRepository,
  QuizRepository,
  CertificateRepository,
  ResourceRepository,
  LessonRepository,
  ModuleRepository,
  MessageRepository,
  NotificationRepository,
  TagRepository,
  SubscriptionRepository,
  AnalyticsRepository,
  CartRepository,
  PurchaseRepository,
  ProfessionalProfileRepository,
  AdminSettingsRepository,
  SupportMessageRepository,
  JobRepository,
  FavoriteRepository,
  EnrollmentRepository,
  CategoryRepository,
  CertificateTemplateRepository,
  TestimonialRepository,
  TenantRepository,
  ExamRepository,
  OrderRepository,
  PaymentRepository,
  EmailVerificationRepository,
  PasswordResetRepository,
  StripeCustomerRepository
} from './repository';
import { CourseRepository } from './repository/course.repository';

// Import all use cases
import {
  CertificationUseCase,
  AuthenticationUseCase,
  QuizUseCase,
  ConnectUseCase,
  CurriculumUseCase,
  ModuleUseCase,
  ResourcesUseCase,
  SubscriptionUseCase,
  ReviewsUseCase,
  NotificationsUseCase,
  AnalyticsUseCase,
  JobUseCase,
  FavoriteUseCase
} from './usecases';
import { CartUseCase } from './usecases/cart.usecase';
import { PurchaseUseCase } from './usecases/purchase.usecase';
import { ProfessionalProfileUseCase } from './usecases/professional-profile.usecase';
import { CertificateUseCase } from './usecases/certificate.usecase';

// Import all services
import { CatalogService } from './catalog/catalog.service';
import { SubscriptionService } from './commerce/subscription.service';
import { OrderService } from './commerce/order.service';
import { SalesService } from './commerce/sales.service';
import { LearningService } from './learning/learning.service';
import { IdentityService } from './identity/identity.service';
import { AdminService } from './admin-service/admin.service';
import { CertificationsService } from './learning/certifications.service';
import { AdminSettingsService } from './admin/admin-settings.service';
import { ResourcesService } from './resources/resources.service';
import { CartService } from './commerce/cart.service';
import { CoursesService } from './courses/courses.service';
import { PurchaseService } from './commerce/purchase.service';
import { ProfessionalProfileService } from './identity/professional-profile.service';
import { CertificateService } from './learning/certificate.service';
import { CertificationSystemService } from './certification-system/certification-system.service';
import { JobsService } from './jobs/jobs.service';
import { JobPostingsService } from './jobs/job-postings.service';
import { SellerService } from './commerce/seller.service';
import { SellerRepository } from './repository/seller.repository';
import { AdminAuditRepository } from './repository/admin-audit.repository';
import { ReviewRepository } from './repository/review.repository';
import { ReviewService } from './reviews/review.service';
import { ProgressRepository } from './repository/progress.repository';
import { ProgressUseCase } from './usecases/progress.usecase';
import { ProgressService } from './learning/progress.service';

// Create repository instances (they use the imported db from @/lib/firebase)
export const analyticsRepository = new AnalyticsRepository();
export const userRepository = new UserRepository();
export const courseRepository = new CourseRepository(analyticsRepository);
export const quizRepository = new QuizRepository();
export const certificateRepository = new CertificateRepository();
export const resourceRepository = new ResourceRepository();
export const lessonRepository = new LessonRepository(analyticsRepository);
export const moduleRepository = new ModuleRepository(analyticsRepository);
export const messageRepository = new MessageRepository(analyticsRepository);
export const notificationRepository = new NotificationRepository(analyticsRepository);
export const tagRepository = new TagRepository(analyticsRepository);
export const subscriptionRepository = new SubscriptionRepository(analyticsRepository);
export const cartRepository = new CartRepository();
export const purchaseRepository = new PurchaseRepository();
export const professionalProfileRepository = new ProfessionalProfileRepository();
export const adminSettingsRepository = new AdminSettingsRepository();
export const supportMessageRepository = new SupportMessageRepository();
export const jobRepository = new JobRepository();
export const favoriteRepository = new FavoriteRepository();
export const enrollmentRepository = new EnrollmentRepository();
export const categoryRepository = new CategoryRepository();
export const certificateTemplateRepository = new CertificateTemplateRepository();
export const testimonialRepository = new TestimonialRepository();
export const tenantRepository = new TenantRepository();
export const examRepository = new ExamRepository();
export const orderRepository = new OrderRepository();
export const paymentRepository = new PaymentRepository();
export const emailVerificationRepository = new EmailVerificationRepository();
export const passwordResetRepository = new PasswordResetRepository();
export const stripeCustomerRepository = new StripeCustomerRepository();
export const progressRepository = new ProgressRepository();
export const sellerRepository = new SellerRepository();
export const adminAuditRepository = new AdminAuditRepository();
export const reviewRepository = new ReviewRepository();

// Create use case instances with their repository dependencies
export const analyticsUseCase = new AnalyticsUseCase(analyticsRepository);

export const authenticationUseCase = new AuthenticationUseCase(userRepository);

export const subscriptionUseCase = new SubscriptionUseCase(
  subscriptionRepository,
  userRepository
);

export const certificationUseCase = new CertificationUseCase(
  courseRepository,
  moduleRepository,
  lessonRepository,
  quizRepository,
  certificateRepository,
  userRepository
);

export const resourcesUseCase = new ResourcesUseCase(
  resourceRepository,
  userRepository
);

export const curriculumUseCase = new CurriculumUseCase(
  resourceRepository,
  userRepository
);

export const quizUseCase = new QuizUseCase(
  quizRepository,
  userRepository,
  courseRepository
);

export const connectUseCase = new ConnectUseCase(
  professionalProfileRepository,
  userRepository
);

export const moduleUseCase = new ModuleUseCase(
  moduleRepository,
  lessonRepository,
  quizRepository,
  courseRepository
);

export const reviewsUseCase = new ReviewsUseCase(userRepository);

export const notificationsUseCase = new NotificationsUseCase(
  userRepository,
  notificationRepository
);

export const cartUseCase = new CartUseCase(cartRepository);

export const purchaseUseCase = new PurchaseUseCase(
  purchaseRepository,
  cartRepository
);

export const professionalProfileUseCase = new ProfessionalProfileUseCase(
  professionalProfileRepository
);

export const certificateUseCase = new CertificateUseCase(
  certificateRepository,
  userRepository
);

export const jobUseCase = new JobUseCase(jobRepository, userRepository);

export const jobPostingsService = new JobPostingsService(jobRepository, userRepository);
export const sellerService = new SellerService(sellerRepository);
export const reviewService = new ReviewService(reviewRepository);

export const favoriteUseCase = new FavoriteUseCase(favoriteRepository);
export const progressUseCase = new ProgressUseCase(progressRepository);

// Create service instances with their use case dependencies
export const identityService = new IdentityService(
  authenticationUseCase,
  subscriptionUseCase,
  userRepository,
  analyticsRepository
);

export const catalogService = new CatalogService(
  certificationUseCase,
  resourcesUseCase,
  curriculumUseCase,
  analyticsUseCase,
  courseRepository
);

export const progressService = new ProgressService(
  progressUseCase,
  analyticsUseCase
);

// Create Subscription Service instance
export const subscriptionService = new SubscriptionService(
  subscriptionUseCase,
  subscriptionRepository,
  userRepository,
  analyticsUseCase,
  purchaseUseCase);


// Create Order Service instance
export const orderService = new OrderService(
  purchaseUseCase,
  cartUseCase,
  userRepository,
  analyticsUseCase
);

// Create Sales Service instance
export const salesService = new SalesService(purchaseRepository);

// LearningService will be defined after CertificationsService to avoid circular dependency


// Create Certifications Service instance with all dependencies
export const certificationsService = new CertificationsService(
  userRepository,
  courseRepository,
  quizRepository,
  certificateRepository,
  lessonRepository,
  moduleRepository,
  resourceRepository,
  notificationRepository,
  analyticsRepository,
  certificationUseCase,
  quizUseCase,
  moduleUseCase,
  notificationsUseCase,
  analyticsUseCase
);

// Create Learning Service instance (after CertificationsService to avoid circular dependency)
export const learningService = new LearningService(
  certificationUseCase,
  quizUseCase,
  moduleUseCase,
  notificationsUseCase,
  analyticsUseCase,
  courseRepository,
  certificationsService
);

// Create Resources Service instance
export const resourcesService = new ResourcesService(
  resourceRepository,
  resourcesUseCase
);

// Create Cart Service instance
export const cartService = new CartService(
  cartUseCase,
  userRepository,
  analyticsUseCase
);

// Create Purchase Service instance
export const purchaseService = new PurchaseService(
  purchaseUseCase,
  userRepository,
  analyticsUseCase
);

// Create Professional Profile Service instance
export const professionalProfileService = new ProfessionalProfileService(
  professionalProfileUseCase,
  analyticsUseCase
);

// Create Certificate Service instance
export const certificateService = new CertificateService(
  certificateUseCase,
  analyticsUseCase
);

// Create Admin Settings Service instance
export const adminSettingsService = new AdminSettingsService(adminSettingsRepository);

// Create Certification System Service instance
export const certificationSystemService = new CertificationSystemService(
  courseRepository,
  moduleRepository,
  lessonRepository,
  resourceRepository,
  quizRepository,
);

// Create Jobs Service instance
export const jobsService = new JobsService(jobUseCase);


// Create Courses Service instance
export const coursesService = new CoursesService(
  courseRepository,
  lessonRepository,
  quizRepository,
  userRepository,
  analyticsRepository,
  certificationUseCase,
  quizUseCase,
  analyticsUseCase
);

// Create Admin Service instance with all dependencies
export const adminService = new AdminService(
  userRepository,
  courseRepository,
  quizRepository,
  certificateRepository,
  resourceRepository,
  lessonRepository,
  moduleRepository,
  messageRepository,
  notificationRepository,
  tagRepository,
  subscriptionRepository,
  analyticsRepository,
  adminAuditRepository,
  certificationsService,
  authenticationUseCase,
  certificationUseCase,
  quizUseCase,
  connectUseCase,
  curriculumUseCase,
  moduleUseCase,
  resourcesUseCase,
  subscriptionUseCase,
  reviewsUseCase,
  notificationsUseCase,
  analyticsUseCase
);

// Export all services, use cases, and repositories for use in panels
export {
  // Services
  CatalogService,
  SubscriptionService,
  OrderService,
  LearningService,
  IdentityService,
  AdminService,
  CertificationsService,
  CartService,
  PurchaseService,
  ProfessionalProfileService,
  CertificateService,
  CoursesService,
  AdminSettingsService,
  JobsService,
  JobPostingsService,
  SellerService,
  ReviewService,

  // Use Cases
  CertificationUseCase,
  AuthenticationUseCase,
  QuizUseCase,
  ConnectUseCase,
  CurriculumUseCase,
  ModuleUseCase,
  ResourcesUseCase,
  SubscriptionUseCase,
  ReviewsUseCase,
  NotificationsUseCase,
  AnalyticsUseCase,
  CartUseCase,
  PurchaseUseCase,
  ProfessionalProfileUseCase,
  CertificateUseCase,
  JobUseCase,
  FavoriteUseCase,

  // Repositories
  UserRepository,
  CourseRepository,
  QuizRepository,
  CertificateRepository,
  ResourceRepository,
  LessonRepository,
  ModuleRepository,
  MessageRepository,
  NotificationRepository,
  TagRepository,
  SubscriptionRepository,
  AnalyticsRepository,
  CartRepository,
  PurchaseRepository,
  ProfessionalProfileRepository,
  AdminSettingsRepository,
  SupportMessageRepository,
  JobRepository,
  FavoriteRepository,
  SellerRepository,
  AdminAuditRepository,
  ReviewRepository
};

export type { JobPosting } from './jobs/job-postings.service';
export type { Review, ReviewStats, ReviewVote } from './reviews/review.types';

// Service instances for easy access
export const services = {
  identity: identityService,
  catalog: catalogService,
  subscription: subscriptionService,
  order: orderService,
  learning: learningService,
  admin: adminService,
  certifications: certificationsService,
  resources: resourcesService,
  courses: coursesService,
  cart: cartService,
  purchase: purchaseService,
  professionalProfile: professionalProfileService,
  progress: progressService,
  certificate: certificateService,
  certificationSystem: certificationSystemService,
  adminSettings: adminSettingsService,
  jobs: jobsService,
  jobPostings: jobPostingsService,
  seller: sellerService,
  review: reviewService
};

// Use case instances for easy access
export const useCases = {
  authentication: authenticationUseCase,
  subscription: subscriptionUseCase,
  certification: certificationUseCase,
  resources: resourcesUseCase,
  curriculum: curriculumUseCase,
  quiz: quizUseCase,
  connect: connectUseCase,
  module: moduleUseCase,
  reviews: reviewsUseCase,
  notifications: notificationsUseCase,
  analytics: analyticsUseCase,
  cart: cartUseCase,
  purchase: purchaseUseCase,
  professionalProfile: professionalProfileUseCase,
  certificate: certificateUseCase,
  job: jobUseCase,
  favorite: favoriteUseCase
};

// Repository instances for easy access
export const repositories = {
  user: userRepository,
  course: courseRepository,
  quiz: quizRepository,
  certificate: certificateRepository,
  resource: resourceRepository,
  lesson: lessonRepository,
  module: moduleRepository,
  message: messageRepository,
  notification: notificationRepository,
  tag: tagRepository,
  subscription: subscriptionRepository,
  analytics: analyticsRepository,
  cart: cartRepository,
  purchase: purchaseRepository,
  professionalProfile: professionalProfileRepository,
  progress: progressRepository,
  adminSettings: adminSettingsRepository,
  supportMessage: supportMessageRepository,
  job: jobRepository,
  favorite: favoriteRepository,
  enrollment: enrollmentRepository,
  category: categoryRepository,
  certificateTemplate: certificateTemplateRepository,
  testimonial: testimonialRepository,
  tenant: tenantRepository,
  exam: examRepository,
  order: orderRepository,
  payment: paymentRepository,
  emailVerification: emailVerificationRepository,
  passwordReset: passwordResetRepository,
  stripeCustomer: stripeCustomerRepository,
  seller: sellerRepository,
  adminAudit: adminAuditRepository,
  review: reviewRepository
};
